import React, { useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { useToast } from "../../hooks/useToast";
import { IntegrationDatasStoreForm } from "../../forms/IntegrationDatasStoreForm";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { Skeleton, SkeletonText } from "@chakra-ui/skeleton";
import { HStack, VStack } from "@chakra-ui/layout";

export const IntegrationDataStore = () => {
  const { businessId, integrationId } = useParams();
  const navigate = useNavigate();
  const apiClient = useAdminApiClient();
  const { showErrorToast, showSuccessToast } = useToast();

  const { data: integration, isLoading } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !integrationId) {
        return null;
      }
      return apiClient.getBusinessIntegration(businessId, integrationId);
    },
    [businessId, integrationId]
  );

  const onSuccess = useCallback(() => {
    showSuccessToast("Integration data store updated.");
    navigate(
      `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`,
      { replace: true }
    );
  }, [showSuccessToast, businessId, integration]);

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Integration data store update failed.");
    },
    [showErrorToast]
  );

  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations`}
            >
              Integrations
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}`}
            >
              {integration?.IntegrationName}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/admin/businesses/${businessId}/integrations/${integration?.id}/data-store`}
            >
              Data Store
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BusinessBreadcrumbs>
        <PageHeading.Title>Integration Data Store</PageHeading.Title>
      </PageHeading>
      <PageContent>
        {(isLoading && (
          <VStack
            w="100%"
            p={6}
            boxShadow="lg"
            alignItems="flex-start"
            spacing={4}
          >
            <Skeleton w="100px" height="40px" />
            <SkeletonText w="100%" mt="4" noOfLines={20} spacing="4" />
            <HStack w="100%">
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
              <Skeleton w="80px" height="40px" />
            </HStack>
          </VStack>
        )) || (
          <IntegrationDatasStoreForm
            businessId={businessId!}
            integrationId={integration?.id!}
            dataStore={integration?.DataStore}
            apiClient={apiClient}
            onCancel={() => {
              navigate(
                `/admin/businesses/${businessId}/integrations/${integration?.Meta?.Id}`
              );
            }}
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        )}
      </PageContent>
    </>
  );
};
